/* eslint-disable */
import React, { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { Button, Box, TextField, Typography, Card, CardContent, Grid } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  background: theme.palette.background.paper,
  boxShadow: theme.customShadows.card,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
}));

const VALUE_LABEL_MAP = {
  CulturallySustainingConversation: 'Culturally sustaining conversation',
  CareerNavigation: 'Career Navigation',
  CollaborationWithConnection: 'Collaboration with connection',
};

const getDisplayValue = (value) => VALUE_LABEL_MAP[value] || value;

const BarChart = ({ data, toast }) => {
  const theme = useTheme();
  const getCurrentYearBounds = () => {
    const now = new Date();
    const year = now.getFullYear();
    const start = new Date(year, 0, 1);
    start.setHours(0, 0, 0, 0);
    const end = new Date(year, 11, 31);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  const formatDateInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [startDate, setStartDate] = useState(getCurrentYearBounds().start);
  const [endDate, setEndDate] = useState(getCurrentYearBounds().end);
  const [startDateInput, setStartDateInput] = useState(formatDateInput(getCurrentYearBounds().start));
  const [endDateInput, setEndDateInput] = useState(formatDateInput(getCurrentYearBounds().end));
  const [filteredData, setFilteredData] = useState([]);
  
  const [msChartData, setMsChartData] = useState({
    options: {
      chart: {
        id: 'ms-bar',
        type: 'bar',
        toolbar: {
          show: false,
        },
        fontFamily: 'inherit',
        foreColor: theme.palette.text.secondary,
      },
      colors: ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b'],
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 6,
          distributed: false,
        },
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
            colors: theme.palette.text.secondary,
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
            colors: theme.palette.text.secondary,
          },
        },
      },
      grid: {
        borderColor: theme.palette.divider,
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '14px',
        fontWeight: 500,
        labels: {
          colors: theme.palette.text.primary,
        },
        markers: {
          width: 12,
          height: 12,
          radius: 6,
        },
      },
      tooltip: {
        theme: theme.palette.mode,
        style: {
          fontSize: '12px',
        },
      },
    },
    series: [],
  });

  const [csChartData, setCsChartData] = useState({
    options: {
      chart: {
        id: 'cs-bar',
        type: 'bar',
        toolbar: {
          show: false,
        },
        fontFamily: 'inherit',
        foreColor: theme.palette.text.secondary,
      },
      colors: ['#fa709a', '#fee140', '#30cfd0', '#330867', '#f5576c'],
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '60%',
          borderRadius: 6,
          distributed: false,
        },
      },
      xaxis: {
        categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500,
            colors: theme.palette.text.secondary,
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          style: {
            fontSize: '12px',
            colors: theme.palette.text.secondary,
          },
        },
      },
      grid: {
        borderColor: theme.palette.divider,
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '14px',
        fontWeight: 500,
        labels: {
          colors: theme.palette.text.primary,
        },
        markers: {
          width: 12,
          height: 12,
          radius: 6,
        },
      },
      tooltip: {
        theme: theme.palette.mode,
        style: {
          fontSize: '12px',
        },
      },
    },
    series: [],
  });

  useEffect(() => {
    const source = Array.isArray(data) ? data : [];
    const filtered = source.filter((entry) => {
      const timestamp = new Date(entry.timestamp);
      return timestamp >= startDate && timestamp <= endDate;
    });
    setFilteredData(filtered);
  }, [data, startDate, endDate]);

  // Update chart theme when theme changes
  useEffect(() => {
    const updateChartTheme = (chartData, setChartData) => {
      setChartData(prev => ({
        ...prev,
        options: {
          ...prev.options,
          chart: {
            ...prev.options.chart,
            foreColor: theme.palette.text.secondary,
          },
          xaxis: {
            ...prev.options.xaxis,
            labels: {
              ...prev.options.xaxis.labels,
              style: {
                ...prev.options.xaxis.labels.style,
                colors: theme.palette.text.secondary,
              },
            },
          },
          yaxis: {
            ...prev.options.yaxis,
            labels: {
              ...prev.options.yaxis.labels,
              style: {
                ...prev.options.yaxis.labels.style,
                colors: theme.palette.text.secondary,
              },
            },
          },
          grid: {
            ...prev.options.grid,
            borderColor: theme.palette.divider,
          },
          legend: {
            ...prev.options.legend,
            labels: {
              colors: theme.palette.text.primary,
            },
          },
          tooltip: {
            ...prev.options.tooltip,
            theme: theme.palette.mode,
          },
        },
      }));
    };

    updateChartTheme(msChartData, setMsChartData);
    updateChartTheme(csChartData, setCsChartData);
  }, [theme.palette.mode, theme.palette.text.primary, theme.palette.text.secondary, theme.palette.divider]);

  const filterData = () => {
    if (!startDate || !endDate || !Array.isArray(data)) return;
    const filtered = data.filter((entry) => {
      const timestamp = new Date(entry.timestamp);
      return timestamp >= startDate && timestamp <= endDate;
    });
    setFilteredData(filtered);
  };

  useEffect(() => {
    updateChartData();
  }, [filteredData]);

  const updateChartData = () => {
    const msSeriesData = {};
    const csSeriesData = {};

    if (Array.isArray(filteredData) && filteredData.length > 0) {
      filteredData.forEach((entry) => {
        entry?.surveyData?.Ms?.forEach((ms) => {
          const displayMs = getDisplayValue(ms);
          if (!msSeriesData[displayMs]) {
            msSeriesData[displayMs] = new Array(12).fill(0);
          }
        });
        entry?.surveyData?.Cs?.forEach((cs) => {
          const displayCs = getDisplayValue(cs);
          if (!csSeriesData[displayCs]) {
            csSeriesData[displayCs] = new Array(12).fill(0);
          }
        });
      });

      filteredData.forEach((entry) => {
        const monthIndex = new Date(entry.timestamp).getMonth();
        entry?.surveyData?.Ms?.forEach((ms) => {
          const displayMs = getDisplayValue(ms);
          if (msSeriesData[displayMs]) {
            msSeriesData[displayMs][monthIndex]++;
          }
        });
        entry?.surveyData?.Cs?.forEach((cs) => {
          const displayCs = getDisplayValue(cs);
          if (csSeriesData[displayCs]) {
            csSeriesData[displayCs][monthIndex]++;
          }
        });
      });
    }

    const msSeries = Object.entries(msSeriesData).map(([name, data]) => ({
      name,
      data,
    }));

    const csSeries = Object.entries(csSeriesData).map(([name, data]) => ({
      name,
      data,
    }));

    setMsChartData((prevChartData) => ({
      ...prevChartData,
      series: msSeries,
    }));

    setCsChartData((prevChartData) => ({
      ...prevChartData,
      series: csSeries,
    }));
  };

  const handleStartDateChange = (e) => {
    const dateValue = e.target.value;
    setStartDateInput(dateValue);
    if (dateValue) {
      const selectedDate = new Date(dateValue);
      selectedDate.setHours(0, 0, 0, 0);
      setStartDate(selectedDate);
    } else {
      setStartDate(null);
    }
  };

  const handleEndDateChange = (e) => {
    const dateValue = e.target.value;
    setEndDateInput(dateValue);
    if (dateValue) {
      const selectedDate = new Date(dateValue);
      selectedDate.setHours(23, 59, 59, 999);
      setEndDate(selectedDate);
    } else {
      setEndDate(null);
    }
  };

  const clearFilter = () => {
    const { start, end } = getCurrentYearBounds();
    setStartDate(start);
    setEndDate(end);
    setStartDateInput(formatDateInput(start));
    setEndDateInput(formatDateInput(end));
    const source = Array.isArray(data) ? data : [];
    const filtered = source.filter((entry) => {
      const timestamp = new Date(entry.timestamp);
      return timestamp >= start && timestamp <= end;
    });
    setFilteredData(filtered);
  };

  const searchCsMs = () => {
    if (!endDate || !startDate) {
      toast.error('Both start and end dates required');
      return;
    }

    if (endDate < startDate) {
      toast.error('End Date must be Greater than Start Date');
      return;
    }
    filterData();
  };

  const hasDatesSelected = startDate && endDate;

  const hasData = msChartData.series.length > 0 || csChartData.series.length > 0;

  return (
    <StyledCard>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.titles', mb: 1 }}>
            M's & C's Analysis
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Track your Mindset and Competency progress over time
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
            alignItems: { xs: 'stretch', sm: 'flex-end' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
              Start Date
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              id="start-date"
              value={startDateInput}
              onChange={handleStartDateChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: '200px' } }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
              End Date
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="date"
              id="end-date"
              value={endDateInput}
              onChange={handleEndDateChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="contained"
              color="main"
              onClick={searchCsMs}
              disabled={!startDate || !endDate}
              sx={{
                height: '40px',
                minWidth: '100px',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Filter
            </Button>
            {hasDatesSelected && (
              <Button
                variant="outlined"
                onClick={clearFilter}
                sx={{
                  height: '40px',
                  minWidth: '100px',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: 'text.secondary',
                  color: 'text.secondary',
                  '&:hover': {
                    borderColor: 'text.secondary',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>

        {hasData ? (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles', mb: 2 }}>
                  M's
                </Typography>
                <Chart
                  options={msChartData.options}
                  series={msChartData.series}
                  type="bar"
                  height={350}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.titles', mb: 2 }}>
                  C's
                </Typography>
                <Chart
                  options={csChartData.options}
                  series={csChartData.series}
                  type="bar"
                  height={350}
                />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.50',
            }}
          >
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1 }}>
              No data available
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Select a date range to view M's & C's analysis
            </Typography>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

export default BarChart;
